// Contract fixtures for the API seam (US-883 · R-7).
//
// When REACT_APP_USE_FIXTURES is on (build-time, CRA), useApi resolves
// requests from this registry instead of the network — static demo
// (Cloudflare Pages has no backend), deterministic tests, and development
// without the Docker stack. Fixtures mirror the contract shape with
// synthetic values only: no production data, no DOCX, no sensitive geodata
// (AGENTS.md §6/§8).
//
// Each feature US registers and owns its fixture (US-879 countryData,
// US-882 reportJob, …); this module ships the mechanism plus the reference
// fixture for optimal-tech-mix — the endpoint no other US owns — proving the
// path end to end. A responder may simulate an API failure by throwing an
// ApiError; it travels the same normalized error path as the network (R-2).
import { matchEndpoint } from "../api/endpoints";
import { ApiError } from "../api/errors";

const registry = new Map(); // endpointKey → responder(body, { method, path, key })

// Registers a responder for a catalog key; returns an unregister function
// (handy for tests). Re-registering a key replaces the previous responder.
export const registerFixture = (endpointKey, responder) => {
  registry.set(endpointKey, responder);
  return () => {
    if (registry.get(endpointKey) === responder) registry.delete(endpointKey);
  };
};

// Build-time flag, read at call time so tests can toggle it per case.
export const fixturesEnabled = () => {
  const flag = (process.env.REACT_APP_USE_FIXTURES || "").toLowerCase();
  return flag === "true" || flag === "1";
};

// Resolves a request from the registry. An endpoint without a fixture while
// the flag is on fails with an explicit ApiError — better than a ghost fetch
// against a backend that is not there.
export const resolveFixture = async (method, path, body) => {
  const key = matchEndpoint(method, path);
  const responder = key ? registry.get(key) : undefined;

  if (!responder) {
    throw new ApiError({
      status: 0,
      message: `No fixture registered for ${method} ${path} (REACT_APP_USE_FIXTURES is on)`,
      detail: "no-fixture-registered",
      path,
    });
  }

  try {
    return await responder(body, { method, path, key });
  } catch (error) {
    if (error instanceof ApiError) throw error; // simulated API failure, pass through
    throw new ApiError({
      status: 0,
      message: `Fixture for ${key} failed`,
      detail: error && error.message ? error.message : null,
      path,
    });
  }
};

// --- Reference fixture: optimal-tech-mix (US-883) ---------------------------
// Response wrapper mirrored from backend/map_stats/views.py (OptimalTechMix):
// { optimal_tech_mix, parameters_used, country_info }. The engine keys follow
// the synthetic shape already canonized by backend/perf/loadtest.py. All
// values are synthetic round numbers.
registerFixture("optimalTechMix", (body = {}) => ({
  optimal_tech_mix: {
    total_fiber_cost_high_urban: 12500000,
    total_fiber_cost_low_urban: 9800000,
    total_fiber_cost_high_rural: 21400000,
    total_fiber_cost_low_rural: 16700000,
    total_fwa_cost_high_urban: 5300000,
    total_fwa_cost_low_urban: 4100000,
    total_fwa_cost_high_rural: 8900000,
    total_fwa_cost_low_rural: 7200000,
    total_satellite_cost_high: 3600000,
    total_satellite_cost_low: 2900000,
    total_capex_high: 51700000,
    total_capex_low: 40700000,
    total_hh_covered_fiber_low_urban: 182000,
    total_hh_covered_fiber_high_urban: 214000,
    total_hh_covered_fiber_low_rural: 96000,
    total_hh_covered_fiber_high_rural: 121000,
  },
  parameters_used: {
    maximum_last_mile_budget: 50000000,
    maximum_spend_4G_perHH: 400,
    avg_hh_size: 4.2,
    labor_scale_rate: 1.0,
    tower_cost_deeprural_USD: 150000,
    tower_cost_lightrural_USD: 120000,
    tower_cost_suburban_USD: 100000,
    tower_cost_urban_USD: 90000,
    tower_labor_cost: 20000,
    satellite_build_cost_pHH: 550,
  },
  country_info: {
    country_name: body.country_name || "PHL",
    admin1: body.admin1 || null,
    admin2: body.admin2 || null,
    admin3: body.admin3 || null,
    admin4: body.admin4 || null,
  },
}));
